import { useCallback } from 'react';
import {
  trackEvent,
  trackButtonClick,
  trackPaymentCompletion,
  trackSearch,
  trackBooking,
  AnalyticsEvent,
} from '@/lib/analytics';
import { useCityFromUrl } from '@/lib/cityUtils';
import { useDateRange } from '@/components/date/DateRangeContext';
import { toLocalISOString } from '@/lib/formatHour';

export interface ClickAnalyticsData {
  city_name: string;
  city_id: string;
  from_date: string;
  to_date: string;
  [key: string]: unknown;
}

export const useAnalytics = () => {
  const currentCity = useCityFromUrl();
  const { range } = useDateRange();

  // Page views are now handled by lib/tracking via AnalyticsProvider
  // to include UTM params and returning visitor status

  const trackEventCallback = useCallback((event: AnalyticsEvent) => {
    trackEvent(event);
  }, []);

  const trackButtonClickCallback = useCallback((
    buttonName: string,
    category?: string,
    customProperties?: Record<string, unknown>
  ) => {
    trackButtonClick(buttonName, category, customProperties);
  }, []);

  const trackPaymentCallback = useCallback((
    amount: number,
    currency?: string,
    paymentMethod?: string,
    bookingId?: string,
    customProperties?: Record<string, unknown>
  ) => {
    trackPaymentCompletion(amount, currency, paymentMethod, bookingId, customProperties);
  }, []);

  const trackSearchCallback = useCallback((
    searchTerm: string,
    category?: string,
    resultsCount?: number,
    customProperties?: Record<string, unknown>
  ) => {
    trackSearch(searchTerm, category, resultsCount, customProperties);
  }, []);

  const trackBookingCallback = useCallback((
    action: 'start' | 'complete' | 'cancel',
    bookingId?: string,
    amount?: number,
    customProperties?: Record<string, unknown>
  ) => {
    trackBooking(action, bookingId, amount, customProperties);
  }, []);

  // Enhanced click analytics functions
  const getBaseAnalyticsData = useCallback((): ClickAnalyticsData => ({
    city_name: currentCity.name || 'none',
    city_id: currentCity._id || 'none',
    from_date: toLocalISOString(range.startDate),
    to_date: toLocalISOString(range.endDate),
  }), [currentCity, range]);

  // Enhanced click tracking with automatic standardized data
  const trackClick = useCallback((
    buttonName: string,
    category: string,
    additionalData?: Record<string, unknown>
  ) => {
    const baseData = getBaseAnalyticsData();
    trackButtonClick(buttonName, category, {
      ...baseData,
      ...additionalData,
    });
  }, [getBaseAnalyticsData]);

  // Predefined tracking functions for common actions
  const trackNavigation = useCallback((
    destination: string,
    additionalData?: Record<string, unknown>
  ) => {
    trackClick(`Navigate_To_${destination}`, 'Navigation', additionalData);
  }, [trackClick]);

  const trackFormAction = useCallback((
    action: string,
    formName: string,
    additionalData?: Record<string, unknown>
  ) => {
    trackClick(`${action}_${formName}`, 'Form_Action', additionalData);
  }, [trackClick]);

  const trackFilterAction = useCallback((
    filterType: string,
    filterValue: string,
    additionalData?: Record<string, unknown>
  ) => {
    trackClick(`Filter_${filterType}`, 'Filter_Action', {
      filter_value: filterValue,
      ...additionalData,
    });
  }, [trackClick]);

  const trackModalAction = useCallback((
    action: 'open' | 'close',
    modalName: string,
    additionalData?: Record<string, unknown>
  ) => {
    trackClick(`${action}_${modalName}_Modal`, 'Modal_Action', additionalData);
  }, [trackClick]);

  const trackTabSwitch = useCallback((
    tabName: string,
    additionalData?: Record<string, unknown>
  ) => {
    trackClick(`Switch_To_${tabName}_Tab`, 'Tab_Navigation', additionalData);
  }, [trackClick]);

  const trackSocialAction = useCallback((
    platform: string,
    action: string,
    additionalData?: Record<string, unknown>
  ) => {
    trackClick(`${action}_${platform}`, 'Social_Action', additionalData);
  }, [trackClick]);

  // Create a click handler wrapper that automatically tracks clicks
  const createClickHandler = useCallback(
    (
      onClick: () => void,
      analyticsName?: string,
      analyticsCategory?: string,
      analyticsData?: Record<string, unknown>
    ) => {
      return () => {
        // Call the original onClick handler
        onClick();
        
        // Track the click event
        trackClick(
          analyticsName || 'Button_Click',
          analyticsCategory || 'User_Interaction',
          analyticsData
        );
      };
    },
    [trackClick]
  );

  return {
    // Original analytics functions
    trackEvent: trackEventCallback,
    trackButtonClick: trackButtonClickCallback,
    trackPayment: trackPaymentCallback,
    trackSearch: trackSearchCallback,
    trackBooking: trackBookingCallback,
    
    // Enhanced click analytics functions
    trackClick,
    trackNavigation,
    trackFormAction,
    trackFilterAction,
    trackModalAction,
    trackTabSwitch,
    trackSocialAction,
    createClickHandler,
    getBaseAnalyticsData,
  };
};
